import React, { useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import Recaptcha, { RecaptchaRef } from "../../components/Recaptcha";
import { recaptchaService } from "../../services/recaptchaService";
import EyeIcon from "../../assets/icone/EyeIcon";
import EyeSlashIcon from "../../assets/icone/EyeSlashIcon";
import "./Login.css";

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { login, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showFirebaseWarning, setShowFirebaseWarning] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string>("");
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const recaptchaRef = useRef<RecaptchaRef>(null);

  // Force Hebrew language for admin login
  React.useEffect(() => {
    if (i18n.language !== "he") {
      i18n.changeLanguage("he");
    }
    document.dir = "rtl";
    document.body.classList.remove("ltr");
    document.body.classList.add("rtl");
  }, [i18n]);

  // אם המשתמש כבר מחובר, ננתב אותו לדף האדמין
  if (!loading && isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
    setRecaptchaVerified(true);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken("");
    setRecaptchaVerified(false);
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken("");
    setRecaptchaVerified(false);
    alert("שגיאה באימות reCAPTCHA. אנא נסה שוב.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Check if reCAPTCHA is verified
      if (!recaptchaVerified || !recaptchaToken) {
        alert("אנא אמת את reCAPTCHA לפני ההתחברות.");
        setIsLoading(false);
        return;
      }

      // Verify reCAPTCHA token with Firebase function
      const verificationResult = await recaptchaService.verifyToken(
        recaptchaToken,
        "admin_login"
      );

      if (!verificationResult.success) {
                alert("אימות האבטחה נכשל. אנא נסה שוב.");

        // Reset reCAPTCHA
        setRecaptchaToken("");
        setRecaptchaVerified(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        setIsLoading(false);
        return;
      }

      // Proceed with login
      await login(email, password);

      // Reset reCAPTCHA on successful login
      setRecaptchaToken("");
      setRecaptchaVerified(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (err: any) {
      if (err.message && err.message.includes("Firebase not configured")) {
        setShowFirebaseWarning(true);
        setError("Firebase לא מוגדר. אנא הגדר את פרויקט Firebase שלך.");
      } else if (err.message && err.message.includes("reCAPTCHA")) {
        alert("שגיאה באימות האבטחה. אנא נסה שוב.");
        // Reset reCAPTCHA on error
        setRecaptchaToken("");
        setRecaptchaVerified(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      } else {
        setError(t("login.invalidCredentials"));
        // Reset reCAPTCHA on login error
        setRecaptchaToken("");
        setRecaptchaVerified(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // מציג loading screen בזמן בדיקת מצב ההתחברות
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        {t("loading")}
      </div>
    );
  }

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={handleSubmit}>
        <h1 className="title">{t("login.title")}</h1>
        {error && <div className="error-message">{error}</div>}
        {showFirebaseWarning && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "4px",
              padding: "10px",
              marginBottom: "15px",
              color: "#856404",
            }}
          >
            <strong>הגדרת Firebase נדרשת:</strong>
            <br />
            1. צור פרויקט Firebase חדש
            <br />
            2. העתק את פרטי הקונפיגורציה לקובץ .env
            <br />
            3. הפעל מחדש את האפליקציה
          </div>
        )}
        <div className="form-group">
          <label className="label" htmlFor="email">
            {t("login.email")}
          </label>
          <input
            className="input"
            id="email"
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            required
          />
        </div>
        <div className="form-group">
          <label className="label" htmlFor="password">
            {t("login.password")}
          </label>
          <div className="password-input-wrapper">
            <input
              className="input password-input"
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              required
            />
            <button
              type="button"
              className="password-toggle-button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
            >
              {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>

        <div className="recaptcha-container">
          <Recaptcha
            ref={recaptchaRef}
            siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
            onVerify={handleRecaptchaVerify}
            onExpired={handleRecaptchaExpired}
            onError={handleRecaptchaError}
            theme="light"
            size="normal"
            className="recaptcha-input"
            language="he"
          />
          {!recaptchaVerified && (
            <div className="recaptcha-message">אנא אמת שאתה לא רובוט</div>
          )}
        </div>

        <button
          className="submit-button"
          type="submit"
          disabled={isLoading || !recaptchaVerified}
        >
          {isLoading ? t("common.loading") : t("login.submit")}
        </button>
      </form>
    </div>
  );
};

export default Login;
